import {
  CYBER_PAYLOADS,
  MAX_INTERCEPT_SALVO,
  cyberActionDef,
  missionDef,
  payloadDef,
  type CyberPayload,
  type MissionId,
} from '@badger-sentinel/rules';
import { MISSION_PARAM_KEYS } from '@badger-sentinel/protocol';

/**
 * The mission vocabulary, as the console says it (Q6).
 *
 * Three tables, each answering a question `MissionDef` cannot:
 *
 *  1. **What it is called on screen** — `MISSION_NAMES`, constant across
 *     scenarios, exhaustive over `MISSION_IDS` by type.
 *  2. **Which wire arm carries its target** — `OWN_ASSET_MISSIONS`.
 *  3. **What its target number is made of** — `tnBasisOf`, which is the one
 *     place `tn: null`'s two meanings are separated.
 */

/**
 * Display names (Q6).
 *
 * `Record<MissionId, string>` rather than a partial map, so a mission added to
 * `MISSION_IDS` is a compile error here rather than a screen reading
 * `ESTABLISH_PERSISTENCE`. Most rows are the id with its underscores taken out,
 * and the table earns its keep on the six that are not: an operator says
 * "EMCON", "BDA", "RPO" and "TT&C", not the enum.
 */
export const MISSION_NAMES: Readonly<Record<MissionId, string>> = Object.freeze({
  WIDE_SEARCH: 'WIDE SEARCH',
  SENSOR_TASK: 'SENSOR TASK',
  CUSTODY_WATCH: 'CUSTODY WATCH',
  CHARACTERIZE: 'CHARACTERIZE',
  DEBRIS_SURVEY: 'DEBRIS SURVEY',
  PHASE_SHIFT: 'PHASE SHIFT',
  TRANSFER: 'TRANSFER',
  SHADOW: 'SHADOW',
  RPO: 'RPO',
  ENGAGE: 'ENGAGE',
  BREAK_CUSTODY: 'BREAK CUSTODY',
  DEPLOY: 'DEPLOY SUB-SATELLITE',
  EMISSION_CONTROL: 'EMCON',
  EVADE: 'EVADE',
  ELECTRONIC_SUPPORT: 'ELECTRONIC SUPPORT',
  ELINT: 'ELINT',
  JAMMING: 'JAMMING',
  DAZZLE: 'DAZZLE',
  ELECTRONIC_PROTECTION: 'ELECTRONIC PROTECTION',
  SPOOF: 'SPOOF',
  RECONNOITER: 'RECONNOITER',
  HUNT: 'HUNT',
  GAIN_ACCESS: 'GAIN ACCESS',
  ESCALATE_PRIVILEGE: 'ESCALATE PRIVILEGE',
  DEPLOY_PAYLOAD: 'DEPLOY PAYLOAD',
  ESTABLISH_PERSISTENCE: 'ESTABLISH PERSISTENCE',
  HARDEN: 'HARDEN',
  MONITOR: 'MONITOR',
  ISOLATE: 'ISOLATE',
  RESTORE: 'RESTORE',
  LAUNCH: 'LAUNCH',
  INTERCEPT: 'INTERCEPT',
  AREA_SEARCH: 'AREA SEARCH',
  POINT_COLLECTION: 'POINT COLLECTION',
  DWELL: 'DWELL',
  TARGET_DEVELOPMENT: 'TARGET DEVELOPMENT',
  BATTLE_DAMAGE_ASSESSMENT: 'BDA',
  PATTERN_OF_LIFE: 'PATTERN OF LIFE',
  RELOCATE: 'RELOCATE',
  TTC_REASSIGN: 'TT&C REASSIGN',
});

export function missionLabel(mission: MissionId): string {
  return MISSION_NAMES[mission];
}

/**
 * What a mission's `targetKinds` are **called**, which is not what they are keyed.
 *
 * `missionLabel` has existed since the builder was drawn and its counterpart never
 * did, so the one place a target kind reaches the operator — the block on a slot
 * filled with the wrong thing — printed the protocol enum: a FIRES cadet on turn 1
 * read `INTERCEPT NEEDS A MW_EVENT`, a phrase in no brief and no rulebook. Every
 * arm of `zOrderTarget` is named here so a new one cannot reach a screen unnamed.
 */
const TARGET_KIND_NAMES: Readonly<Record<string, string>> = Object.freeze({
  NONE: 'NO TARGET',
  SELF: 'ITSELF',
  BAND: 'ORBITAL BAND',
  REGION: 'REGION',
  CONTACT: 'CONTACT',
  OWN_ASSET: 'OWN ASSET',
  SLOT: 'ORBITAL SLOT',
  MW_EVENT: 'LAUNCH EVENT',
});

export function targetKindLabel(kind: string): string {
  return TARGET_KIND_NAMES[kind] ?? kind;
}

/** `A REGION`, `AN ORBITAL BAND` — the article the sentence needs, not a rule engine. */
export function targetKindPhrase(kind: string): string {
  const name = targetKindLabel(kind);
  return ('AEIOU'.includes(name.charAt(0)) ? 'AN ' : 'A ') + name;
}

/** The one-line description the picker shows beside the name. */
export function missionSummary(mission: MissionId): string {
  return missionDef(mission)?.summary ?? '';
}

/**
 * Missions whose target is one of the seat's **own** assets — the `OWN_ASSET`
 * arm of `zOrderTarget` (Q24), landed in Phase 6.5.
 *
 * Until that arm existed these eight were **withheld from the picker entirely**,
 * because the wire could not express them: `CONTACT` carries a `zContactAlias`
 * and `aliasTableFor` builds its table from enemy assets plus this team's own
 * phantoms, so an order naming one's own station failed the schema before it
 * reached the server. That set was called `FRIENDLY_TARGET_MISSIONS`, and this
 * is the same eight ids doing the opposite job: they are no longer refused, they
 * are pointed at a different list.
 *
 * The alias boundary is unchanged, which is the whole reason for a second arm
 * rather than a looser `CONTACT`: a `CONTACT` target still means *"something you
 * were never told the identity of"*, and an `OWN_ASSET` target means *"a machine
 * out of your own order of battle"*. The server checks the second claim, with
 * one message for "not yours" and "does not exist" alike so the arm cannot be
 * used to probe for enemy ids.
 *
 * Five of the eight are derivable — `CYBER_ACTIONS[m].offensive === false` — and
 * the list is written out anyway, with a test holding the two against each
 * other. A derived set would silently follow a rules-table edit; a written one
 * fails, which is what a boundary this narrow is for.
 */
export const OWN_ASSET_MISSIONS: ReadonlySet<MissionId> = Object.freeze(
  new Set<MissionId>([
    // RB §5.6.5 — the defensive cyber half, all against one's own machines.
    'HUNT',
    'HARDEN',
    'MONITOR',
    'ISOLATE',
    'RESTORE',
    // RB §5.5.2 — "Protects one friendly asset" (`phase2-em.ts`: owner check).
    'ELECTRONIC_PROTECTION',
    // Errata #13 — a sub-satellite this launcher carries.
    'DEPLOY',
    // Errata #11 — the new station takes over one of its own team's satellites.
    'TTC_REASSIGN',
  ]),
);

/**
 * Does this mission's `CONTACT` target mean a friendly id rather than an alias?
 *
 * The builder asks it in two places — which rows to offer for the target slot,
 * and which arm to build — so the question is one function and not two reads of
 * the set that will one day disagree.
 */
export function targetsOwnAsset(mission: MissionId): boolean {
  return OWN_ASSET_MISSIONS.has(mission);
}

// ---------------------------------------------------------------------------
// Mission parameters (`MISSION_PARAM_KEYS`)
// ---------------------------------------------------------------------------

/**
 * The fourth thing an order can say, and the two the engine actually reads.
 *
 * `MISSION_PARAM_KEYS` names five missions. Three of them are **not read by any
 * resolver in this build**, and shipping a control for them would be a control
 * whose value changes nothing:
 *
 * | Key | What reads it |
 * | --- | --- |
 * | `DEPLOY_PAYLOAD.payload` | `phase1-cyber.ts` — required; a missing one is `BAD_PARAMETERS` |
 * | `INTERCEPT.missiles` | `phase7-kinetic.ts` `clampSalvo` — optional, defaults to the cap |
 * | `ESCALATE_PRIVILEGE.to` | **nothing** — `resolveOffensive` climbs from *current* access |
 * | `TRANSFER.destSlot` | **nothing** — the `SLOT` target already carries band and slot |
 * | `RELOCATE.region` | **nothing** — the `REGION` target already carries it |
 *
 * The last three are the same value said twice, or a value the engine ignores.
 * So the parameter is not a fourth slot: it is a **second line inside the
 * mission slot**, present only for a mission that reads one, and the picker for
 * it opens the same way the mission picker does. Three slots stay three.
 */
export interface MissionParamOption {
  readonly value: string | number;
  readonly label: string;
  readonly detail: string;
}

export interface MissionParamSpec {
  readonly key: string;
  readonly label: string;
  /** A mission the engine refuses without this parameter cannot be queued without it. */
  readonly required: boolean;
  readonly options: readonly MissionParamOption[];
}

const PAYLOAD_OPTIONS: readonly MissionParamOption[] = Object.freeze(
  CYBER_PAYLOADS.map((payload: CyberPayload) => {
    const def = payloadDef(payload);
    return {
      value: payload,
      label: payload,
      detail: def.requiresAccess + ' · ESC +' + String(def.escalation) + ' · ' + def.summary,
    };
  }),
);

const SALVO_OPTIONS: readonly MissionParamOption[] = Object.freeze(
  Array.from({ length: MAX_INTERCEPT_SALVO }, (_unused, index) => {
    const count = index + 1;
    return {
      value: count,
      label: String(count),
      detail: count === 1 ? 'ONE MISSILE COMMITTED' : String(count) + ' MISSILES COMMITTED',
    };
  }),
);

const PARAM_SPECS: Readonly<Partial<Record<MissionId, MissionParamSpec>>> = Object.freeze({
  DEPLOY_PAYLOAD: {
    key: MISSION_PARAM_KEYS.DEPLOY_PAYLOAD[0],
    label: 'PAYLOAD',
    required: true,
    options: PAYLOAD_OPTIONS,
  },
  INTERCEPT: {
    key: MISSION_PARAM_KEYS.INTERCEPT[0],
    label: 'MISSILES',
    /**
     * Required in the builder, though not in the engine.
     *
     * `clampSalvo` defaults an absent value to `MAX_INTERCEPT_SALVO` and every
     * committed missile is expended (RB §5.7.5), so an order queued without a
     * choice spends four rounds of a scarce magazine by omission. The engine
     * keeps its default for any order that arrives without one; the console
     * makes the seat say the number.
     */
    required: true,
    options: SALVO_OPTIONS,
  },
});

export function paramSpecOf(mission: MissionId): MissionParamSpec | null {
  return PARAM_SPECS[mission] ?? null;
}

// ---------------------------------------------------------------------------
// `tn: null` means two different things (data/missions.ts, its own header)
// ---------------------------------------------------------------------------

/**
 * > For `PHASE_SHIFT` it means *deterministic, no roll*. For the cyber actions
 * > and `LAUNCH` it means *the TN is not a constant*.
 *
 * Two states that must never be drawn the same way. A ladder that reads `null`
 * as "no chance computable" lies about `PHASE_SHIFT`, which always happens; one
 * that reads it as "certain" lies about `LAUNCH`, which is a roll nobody here
 * can price.
 *
 * `INTERCEPT` is a **third** member of the variable family that
 * `data/missions.ts`'s header does not name: it rolls against the inbound's
 * `WeaponDef.interceptTn`, which the seat learns from the release and not from
 * this column. It is resolved here as variable for that reason, with its own
 * basis, rather than falling through to deterministic — which is what a
 * hand-written list of "cyber plus LAUNCH" would have done.
 */
export type TnBasis =
  /** A flat number from `MissionDef.tn`. */
  | { readonly kind: 'FLAT'; readonly tn: number }
  /** No roll happens at all. The mission resolves. */
  | { readonly kind: 'DETERMINISTIC' }
  /** A roll happens against a number this seat has not been given. */
  | { readonly kind: 'VARIABLE'; readonly source: string; readonly detail: string };

export function tnBasisOf(mission: MissionId): TnBasis {
  const def = missionDef(mission);
  if (def === undefined)
    return { kind: 'VARIABLE', source: 'UNRESOLVED', detail: 'NOT IN THIS BUILD' };
  if (def.tn !== null) return { kind: 'FLAT', tn: def.tn };

  // Derived from the cyber table rather than from a list of ids: an action with a
  // TN *offset* rolls against the target's hardening (`data/cyber.ts`), and one
  // with neither an offset nor a flat TN does not roll at all.
  const cyber = cyberActionDef(mission);
  if (cyber !== undefined && cyber.tnOffset !== null) {
    return {
      kind: 'VARIABLE',
      source: 'HARDENING',
      detail: "TARGET'S HARDENING · RECONNOITER REVEALS IT",
    };
  }

  if (mission === 'LAUNCH') {
    return { kind: 'VARIABLE', source: 'PNT', detail: 'PNT CONSTELLATION HEALTH (RB §5.7.6)' };
  }
  if (mission === 'INTERCEPT') {
    return { kind: 'VARIABLE', source: 'INBOUND', detail: "THE INBOUND'S TYPE (RB §5.7.5)" };
  }

  return { kind: 'DETERMINISTIC' };
}
